'use client';

import Link from 'next/link';

import React, { ForwardedRef, MutableRefObject, memo, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

import { markdownToComponent } from '@/utils/convertMarkdown';
import formatURL, { formatURLWithPath } from '@/utils/format/formatURL';
import getIcon from '@/utils/getIcon';
import { getManagedDatagridSetters } from '@/utils/hooks/useManagedDatagrid';
//Icons and icon key
import { ChevronDown } from '@/utils/lucide';
import { ChevronUp } from '@/utils/lucide';
import { ChevronsDownUp } from '@/utils/lucide';
import openDetail from '@/utils/openDetail';
import truncateMarkdown from '@/utils/truncateMarkdown';

import { addFieldState, setFieldState } from '@/redux/interactionSlice';
import store, { RootState } from '@/redux/store';

import OpenableTitle from '../Layout/OpenableTitle';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { ListCellProps } from './columns';

const ListCell = ({ value, row, column, cell, table }: ListCellProps) =>
{
	//Store in redux
	const key = `${row.original.id}-${column.columnDef.accessorKey}`;
	const initialOpen = store.getState().interaction.fieldState[key] ?? false;
	const [open, setOpen] = useState(initialOpen);

	const shortAnswers = table.options.meta?.shortAnswers;

	//Threshold - if the items are longer than this, show a 'show more' button.
	//However we only want the show more button to show if the items count is meaningfully exceeding than the threshold. (that way the show more doesn't just show one extra item)
	const hasLogos = false; // value.some((item: any) => item.logo_url);
	const threshold = shortAnswers ? 2 : hasLogos ? 3 : 4;
	const thresholdOffset = hasLogos ? 1 : 1;
	const sliceLength = value.length <= threshold + thresholdOffset || (open && !shortAnswers) ? value.length : threshold;
	const isTruncated = value.length > sliceLength;

	return (
		<div className='flex flex-col'>
			<div className=''>
				<ul className='list-disc pl-4'>
					{value.slice(0, sliceLength).map((item: any, index: number) =>
					{
						//List items can be objects (companies, drugs, etc)
						let display;

						if (typeof item === 'string')
						{
							display = (
								<li className='' key={index}>
									{markdownToComponent(item)}
								</li>
							);
						}
						else if (typeof item === 'number' || typeof item === 'bigint' || typeof item === 'boolean')
						{
							display = (
								<li>
									<p>{`${item}`}</p>
								</li>
							);
						}
						else if (item.id)
						{
							display = (
								<li className='' key={index}>
									<OpenableTitle title={item.title ?? item.name} id={item.id} type={item.type} noIcon />
								</li>
							);
						}
						else if (item.name)
						{
							display = (
								<li className='' key={index}>
									{item.name}
								</li>
							);
						}
						else
						{
							display = (
								<li key={index}>
									<p>{JSON.stringify(item)}</p>
								</li>
							);
						}

						return display;
					})}
				</ul>
				{shortAnswers && isTruncated && (
					<div
						className='absolute -bottom-0.5 -right-0.5 text-gray-300 cursor-pointer'
						onClick={e =>
						{
							const id = table.options.meta?.identifier;
							const { setExpanded } = getManagedDatagridSetters(id);
							setExpanded(true);
						}}
					>
						<ChevronsDownUp className='text-icon-sm' />
					</div>
				)}
			</div>
			{!shortAnswers && value.length > threshold + thresholdOffset && (
				<button
					className='flex flex-row items-center justify-center px-0 py-2 text-nowrap text-xs text-gray-400 hover:text-gray-700 '
					onClick={e =>
					{
						console.log('Toggling open', open);
						//setOpen(!open);
						store.dispatch(
							addFieldState({
								key,
								value: !open
							})
						);
						setOpen(!open);
						e.stopPropagation();
					}}
				>
					{open ? (
						<p className='flex flex-row'>
							Show Less{' '}
							<span className='text-icon-sm pl-2'>
								<ChevronUp />
							</span>
						</p>
					) : (
						<p className='flex flex-row'>
							Show More ({value.length - threshold}){' '}
							<span className='text-icon-sm pl-2'>
								<ChevronDown />
							</span>
						</p>
					)}
				</button>
			)}
		</div>
	);
};

export default ListCell;
